import React, { useState } from 'react';
import { Plus, X, Tag } from 'lucide-react';

/**
 * Alıcıya ait özel alanları (custom_fields) düzenler.
 *
 * Bu alanlar e-posta şablonlarında {{alan_adi}} şeklinde kullanılabilir.
 * Excel içe aktarımında Email/İsim/Grup dışındaki kolonlar otomatik olarak
 * buraya düşer.
 */
const CustomFieldsEditor = ({ value = {}, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [error, setError] = useState('');

  const entries = Object.entries(value || {});

  // Şablon değişkeni olarak kullanılabilmesi için ad normalize edilir.
  const normalizeKey = (raw) => {
    const trMap = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' };
    return raw
      .trim()
      .toLowerCase()
      .replace(/[çğıöşü]/g, (c) => trMap[c] || c)
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const addField = () => {
    const key = normalizeKey(newKey);
    if (!key) return;

    if (key === 'name' || key === 'email') {
      setError('"name" ve "email" yerleşik değişkenlerdir, özel alan olarak eklenemez.');
      return;
    }

    if (Object.prototype.hasOwnProperty.call(value || {}, key)) {
      setError(`"${key}" alanı zaten var.`);
      return;
    }

    setError('');
    onChange({ ...value, [key]: newValue.trim() });
    setNewKey('');
    setNewValue('');
  };

  const removeField = (key) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const updateValue = (key, val) => {
    onChange({ ...value, [key]: val });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Formun tamamını göndermesini engelle
      e.preventDefault();
      addField();
    }
  };

  const preview = normalizeKey(newKey);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag size={16} className="text-[#8b5cf6]" />
        <label className="text-sm font-medium text-[#334155]">
          Özel Alanlar
        </label>
        <span className="text-xs text-[#94a3b8]">
          şablonda {'{{alan_adi}}'} olarak kullanılır
        </span>
      </div>

      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(([key, val]) => (
            <div key={key} className="flex items-center gap-2">
              <code className="shrink-0 px-2 py-1.5 bg-[#f5f3ff] text-[#5b21b6] rounded text-xs font-mono border border-[#e9d5ff]">
                {`{{${key}}}`}
              </code>
              <input
                type="text"
                value={val}
                onChange={(e) => updateValue(key, e.target.value)}
                placeholder="değer"
                className="flex-1 px-3 py-1.5 border border-[#e2e8f0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
              />
              <button
                type="button"
                onClick={() => removeField(key)}
                className="p-1.5 text-[#94a3b8] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Alanı sil"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="alan adı (örn: şirket)"
          className="w-40 px-3 py-1.5 border border-dashed border-[#cbd5e1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
        />
        <input
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="değer"
          className="flex-1 px-3 py-1.5 border border-dashed border-[#cbd5e1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
        />
        <button
          type="button"
          onClick={addField}
          disabled={!preview}
          className="p-1.5 text-[#6366f1] hover:bg-[#eef2ff] rounded disabled:opacity-40 transition-colors"
          title="Alan ekle"
        >
          <Plus size={18} />
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {!error && preview && preview !== newKey.trim().toLowerCase() && (
        <p className="text-xs text-[#64748b]">
          Şablonda şöyle kullanılacak:{' '}
          <code className="text-[#5b21b6]">{`{{${preview}}}`}</code>
        </p>
      )}
    </div>
  );
};

export default CustomFieldsEditor;
